import { Flex, Text } from "@chakra-ui/react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import { useGlossary } from "src/hooks/useGlossary";
import {
  SortPagination,
  SortType,
  UNSORTED,
  useLocalData,
} from "src/hooks/useLocalData";
import { MessageStatusTag } from "src/scenes/orgAdmin/messages/components/MessageStatusTag";
import * as GQL from "src/types/graphql";
import { PaginatedTable } from "./PaginatedTable";

export type RecipientsTableDataType = {
  id: uuid;
  full_name: string;
  applicant_name: string;
  contact_info: string;
  preferred_language: string | null;
  message_status?: GQL.message_status_enum;
};

type RecipientsTableProps = {
  recipients: RecipientsTableDataType[];
};

export const RecipientsTable: React.FC<RecipientsTableProps> = ({
  recipients,
}) => {
  const { pathname } = useLocation();
  const isViewPage = useMemo(() => pathname.includes("view"), [pathname]);
  const getLanguageName = useGetEnglishLanguageName();
  const { glossary } = useGlossary();

  const [pagination, setPagination] = useState<SortPagination>({
    limit: 25,
    offset: 0,
    sortKey: "full_name",
    sortType: UNSORTED,
  });

  const [pagedRecipients, setPagedRecipients] = useState<
    RecipientsTableDataType[]
  >([]);

  const [getSortedAndPaginatedData] = useLocalData(recipients);

  const onChangeSort = (columnName: string, sortType: SortType) => {
    const updatedPagination = {
      limit: pagination.limit,
      offset: pagination.offset,
      sortKey: columnName,
      sortType,
    };

    setPagedRecipients(getSortedAndPaginatedData(updatedPagination));
    setPagination(updatedPagination);
  };

  const onFetchMore = (limit: number, offset: number) => {
    const updatedPagination = {
      offset,
      limit,
      sortKey: pagination.sortKey,
      sortType: pagination.sortType,
    };

    setPagedRecipients(getSortedAndPaginatedData(updatedPagination));
    setPagination(updatedPagination);
  };

  useEffect(() => {
    setPagedRecipients(getSortedAndPaginatedData(pagination));
  }, [pagination, getSortedAndPaginatedData]);

  const columns: ColumnDef<RecipientsTableDataType>[] = useMemo(() => {
    return [
      {
        id: "full_name",
        header: `to: ${glossary`parent`}`,
        accessorFn: (row) => row.full_name,
      },
      {
        id: "contact_info",
        header: "contact information",
        cell: (props) => (
          <Text
            maxW="12rem"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            overflow="hidden"
            title={props.row.original.contact_info}
          >
            {props.row.original.contact_info}
          </Text>
        ),
      },
      {
        id: "applicant_name",
        header: glossary`applicant`,
        accessorFn: (row) => row.applicant_name,
      },
      {
        id: "preferred_language",
        header: "preferred language",
        cell: (props) =>
          props.row.original.preferred_language ? (
            getLanguageName(props.row.original.preferred_language)
          ) : (
            <Text variant="placeholder">No preferred language</Text>
          ),
      },
      ...(isViewPage
        ? [
            {
              id: "message_status",
              header: "status",
              cell: ({ row }: { row: Row<RecipientsTableDataType> }) => (
                <MessageStatusTag status={row.original.message_status} />
              ),
            },
          ]
        : []),
    ];
  }, [isViewPage, getLanguageName, glossary]);

  return (
    <Flex direction="column" gap={4} width="100%">
      <Text as="h2" fontWeight="700" fontSize="lg">
        Recipients
      </Text>
      <PaginatedTable<RecipientsTableDataType>
        data={pagedRecipients}
        columns={columns}
        limit={pagination.limit}
        offset={pagination.offset}
        count={recipients.length}
        onFetchMore={onFetchMore}
        sortableColumnIds={[
          "full_name",
          "contact_info",
          "applicant_name",
          "preferred_language",
          "message_status",
        ]}
        onChangeSort={onChangeSort}
      />
    </Flex>
  );
};
